// Customer register form config: fields, options, the empty (new) form and the
// edit mapper. Kept in a JSX-free module so the office UI (CustomersPage) and the
// unit tests share one source of truth. Plain English, zero jargon -- the office
// manager uses this.
import type { FieldDef } from "./RegisterPage";
import type { Customer } from "../../../lib/resources";

// Direct customer (default) vs facilities-management intermediary. The VALUES match
// the API enum exactly (lowercase): the server validates z.enum(["direct",
// "intermediary"]) and the DB enum/default is "direct". The labels are what the
// office sees. (The brief's DIRECT/INTERMEDIARY are display intent; the live API
// accepts only the lowercase values.)
export const clientTypeOptions: { value: string; label: string }[] = [
  { value: "direct", label: "Direct" },
  { value: "intermediary", label: "Intermediary" },
];

const CLIENT_TYPE_HELP =
  "Intermediary clients receive a different service agreement with per-job invoicing. " +
  "Select Direct for all standard maintenance customers.";

export const customerFields: FieldDef[] = [
  { name: "name", label: "Customer name", type: "text", required: true },
  { name: "clientType", label: "Client type", type: "select", options: clientTypeOptions, help: CLIENT_TYPE_HELP },
  { name: "billingContact", label: "Billing contact", type: "text" },
  { name: "billingEmail", label: "Billing email", type: "email" },
  { name: "phone", label: "Phone", type: "text" },
  // Secondary contact (e.g. a building director). When the secondary email is set it
  // is copied on every customer-facing email the platform sends.
  { name: "secondaryContactName", label: "Secondary contact name", type: "text" },
  { name: "secondaryContactRole", label: "Secondary contact role", type: "text" },
  { name: "secondaryContactEmail", label: "Secondary contact email", type: "email" },
  { name: "secondaryContactPhone", label: "Secondary contact phone", type: "text" },
  { name: "notes", label: "Notes", type: "textarea" },
];

// A new customer defaults to Direct (pre-selected), matching the DB default so the
// office never has to set it for a standard maintenance customer.
export const customerEmptyForm: Record<string, string> = {
  name: "",
  clientType: "direct",
  billingContact: "",
  billingEmail: "",
  phone: "",
  secondaryContactName: "",
  secondaryContactRole: "",
  secondaryContactEmail: "",
  secondaryContactPhone: "",
  notes: "",
};

// Edit form: pre-populate from the row. Fall back to Direct if clientType is somehow
// absent so the select never renders blank.
export function customerToForm(c: Customer): Record<string, string> {
  return {
    name: c.name ?? "",
    clientType: c.clientType ?? "direct",
    billingContact: c.billingContact ?? "",
    billingEmail: c.billingEmail ?? "",
    phone: c.phone ?? "",
    secondaryContactName: c.secondaryContactName ?? "",
    secondaryContactRole: c.secondaryContactRole ?? "",
    secondaryContactEmail: c.secondaryContactEmail ?? "",
    secondaryContactPhone: c.secondaryContactPhone ?? "",
    notes: c.notes ?? "",
  };
}
